import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  MessageSquareQuote,
  ExternalLink,
  PenLine,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { StarDisplay, StarInput } from "@/components/reviews/Stars";
import { drywallServices, r3Services } from "@/data/content";
import { submitReview, fetchPublicReviews, fetchPublicSettings } from "@/lib/api";

const inputCls =
  "w-full border border-white/10 bg-charcoal px-5 py-4 text-sm text-white placeholder:text-neutral-600 transition-colors duration-300 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40";

const chipCls = (active: boolean) =>
  `cursor-pointer rounded-full border px-4 py-2.5 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-300 ${
    active
      ? "border-gold bg-gold text-ink"
      : "border-white/15 text-neutral-300 hover:border-gold/50 hover:text-gold"
  }`;

const PROJECT_TYPES = ["residential", "commercial", "newConstruction", "remodel", "other"];

interface PublicReview {
  id: string;
  name: string;
  company?: string | null;
  brand?: string;
  project_type?: string | null;
  service_used?: string | null;
  rating: number;
  review: string;
  city?: string | null;
  featured: boolean;
  created_at: string;
}

export default function ReviewsPage() {
  const { t, lang } = useLanguage();
  const [reviews, setReviews] = useState<PublicReview[] | null>(null);
  const [googleUrl, setGoogleUrl] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    company: "",
    projectType: "residential",
    service: "",
    brand: "r3stan",
    rating: 0,
    review: "",
    city: "",
    contact: "",
    confirmed: false,
  });
  const [status, setStatus] = useState<"idle" | "sending" | "done">("idle");
  const [hp, setHp] = useState("");
  const [otherText, setOtherText] = useState("");

  useEffect(() => {
    fetchPublicReviews()
      .then((r) => setReviews(r.data.reviews))
      .catch(() => setReviews([]));
    fetchPublicSettings()
      .then((r) => setGoogleUrl(r.data.google_review_url || null))
      .catch(() => setGoogleUrl(null));
  }, []);

  const serviceLabel = (slug?: string | null) => {
    if (!slug) return null;
    return t.services.items[slug]?.title || t.r3.items[slug]?.title || slug;
  };

  const brandLabel = (b?: string) => (b === "r3" ? "R3 Services" : "R3stan Drywall, Inc.");

  const dateFmt = (iso: string) =>
    new Date(iso).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const scrollToForm = () =>
    document.querySelector("#leave-a-review")?.scrollIntoView({ behavior: "smooth" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!form.confirmed) {
      toast.error(t.reviews.confirmRequired);
      return;
    }
    if (form.rating < 1) {
      toast.error(`${t.reviews.rating}`);
      return;
    }
    setStatus("sending");
    try {
      await submitReview({
        name: form.name,
        company: form.company,
        project_type: form.projectType,
        service_used: form.service,
        brand: form.brand,
        rating: form.rating,
        review: form.review,
        city: form.city,
        contact: form.contact,
        confirmed: form.confirmed,
        language: lang,
        website: hp,
        other_text: form.projectType === "other" ? otherText : "",
      });
      setStatus("done");
    } catch {
      toast.error(t.reviews.error);
      setStatus("idle");
    }
  };

  return (
    <main data-testid="reviews-page" className="bg-ink pb-28 pt-32 md:pt-40">
      <div className="mx-auto max-w-5xl px-5 md:px-8">
        <Reveal>
          <SectionLabel text={t.reviews.label} testId="reviews-label" />
          <h1
            data-testid="reviews-heading"
            className="mt-5 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
          >
            {t.reviews.heading}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-neutral-400">
            {t.reviews.sub}
          </p>
        </Reveal>

        {googleUrl && (
          <Reveal delay={0.05}>
            <div
              data-testid="google-review-section"
              className="mt-10 flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-charcoal p-6 md:p-8"
            >
              <p className="font-display text-xl font-black uppercase tracking-tight text-white">
                {t.reviews.googleTitle}
              </p>
              <a
                data-testid="google-review-btn"
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
              >
                {t.reviews.googleCta}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </Reveal>
        )}

        <div className="mt-14" data-testid="reviews-list">
          {reviews === null ? null : reviews.length === 0 ? (
            <Reveal>
              <div
                data-testid="reviews-empty-state"
                className="border border-dashed border-white/15 bg-charcoal p-10 text-center md:p-14"
              >
                <MessageSquareQuote className="mx-auto h-12 w-12 text-gold" strokeWidth={1.5} />
                <h2 className="mt-6 font-display text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
                  {t.reviews.emptyTitle}
                </h2>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400 md:text-base">
                  {t.reviews.emptyText}
                </p>
                <button
                  data-testid="reviews-empty-leave-btn"
                  onClick={scrollToForm}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-xs font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark"
                >
                  <PenLine className="h-4 w-4" />
                  {t.reviews.leaveReview}
                </button>
              </div>
            </Reveal>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={(i % 2) * 0.08}>
                  <article
                    data-testid={`review-card-${r.id}`}
                    className="flex h-full flex-col border border-white/10 bg-charcoal p-7 md:p-9"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <StarDisplay rating={r.rating} testId={`review-stars-${r.id}`} />
                      {r.featured && (
                        <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] text-gold">
                          {t.reviews.featuredLabel}
                        </span>
                      )}
                    </div>
                    <p className="mt-5 flex-1 text-sm leading-relaxed text-neutral-300 md:text-base">
                      “{r.review}”
                    </p>
                    <div className="mt-6 border-t border-white/10 pt-5">
                      <p className="font-display text-lg font-black tracking-tight text-white">
                        {r.name}
                        {r.company && (
                          <span className="ml-2 text-sm font-bold text-neutral-400">
                            · {r.company}
                          </span>
                        )}
                      </p>
                      <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                        {brandLabel(r.brand)}
                        {serviceLabel(r.service_used) && ` · ${serviceLabel(r.service_used)}`}
                        {r.city && ` · ${r.city}`}
                      </p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-neutral-600">
                        {dateFmt(r.created_at)}
                      </p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>

        <div id="leave-a-review" className="mt-20 scroll-mt-28 md:mt-28">
          <Reveal>
            <SectionLabel text={t.reviews.leaveReview} testId="review-form-label" />
            <h2
              data-testid="review-form-heading"
              className="mt-5 font-display text-3xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-5xl"
            >
              {t.reviews.formTitle}
            </h2>
            <p className="mt-4 text-sm text-neutral-500">{t.reviews.formSub}</p>
          </Reveal>

          {status === "done" ? (
            <motion.div
              data-testid="review-success-panel"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 border border-gold/30 bg-charcoal p-10 text-center md:p-14"
            >
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.5} />
              <h3 className="mt-6 font-display text-3xl font-black uppercase tracking-tight text-white">
                {t.reviews.successTitle}
              </h3>
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-neutral-400 md:text-base">
                {t.reviews.successText}
              </p>
              <button
                data-testid="review-again-btn"
                onClick={() => {
                  setStatus("idle");
                  setForm({
                    name: "",
                    company: "",
                    projectType: "residential",
                    service: "",
                    brand: "r3stan",
                    rating: 0,
                    review: "",
                    city: "",
                    contact: "",
                    confirmed: false,
                  });
                }}
                className="mt-8 rounded-full border border-white/20 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                {t.reviews.again}
              </button>
            </motion.div>
          ) : (
            <Reveal delay={0.1}>
              <form
                data-testid="review-form"
                onSubmit={onSubmit}
                className="mt-10 space-y-8 border border-white/10 bg-[#0d0d0d] p-6 md:p-10"
              >
                <input
                  type="text"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t.reviews.name}
                    </label>
                    <input
                      data-testid="review-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t.reviews.company}
                    </label>
                    <input
                      data-testid="review-company-input"
                      value={form.company}
                      onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <fieldset>
                  <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.reviews.brand}
                  </legend>
                  <div className="flex flex-wrap gap-2.5">
                    <button
                      type="button"
                      data-testid="review-brand-r3stan"
                      onClick={() => setForm((f) => ({ ...f, brand: "r3stan" }))}
                      className={chipCls(form.brand === "r3stan")}
                    >
                      R3stan Drywall, Inc.
                    </button>
                    <button
                      type="button"
                      data-testid="review-brand-r3"
                      onClick={() => setForm((f) => ({ ...f, brand: "r3" }))}
                      className={chipCls(form.brand === "r3")}
                    >
                      R3 Services
                    </button>
                  </div>
                </fieldset>

                <div className="grid gap-5 sm:grid-cols-2">
                  <fieldset>
                    <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t.reviews.projectType}
                    </legend>
                    <div className="flex flex-wrap gap-2.5">
                      {PROJECT_TYPES.map((pt) => (
                        <button
                          key={pt}
                          type="button"
                          data-testid={`review-projecttype-${pt}`}
                          onClick={() => setForm((f) => ({ ...f, projectType: pt }))}
                          className={chipCls(form.projectType === pt)}
                        >
                          {t.reviews.projectTypes[pt]}
                        </button>
                      ))}
                    </div>
                    {form.projectType === "other" && (
                      <input
                        data-testid="review-other-input"
                        required
                        value={otherText}
                        onChange={(e) => setOtherText(e.target.value)}
                        placeholder={t.reviews.otherDescribe}
                        className={`${inputCls} mt-4`}
                      />
                    )}
                  </fieldset>
                  <div>
                    <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t.reviews.serviceUsed}
                    </label>
                    <select
                      data-testid="review-service-select"
                      value={form.service}
                      onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="">—</option>
                      <optgroup label={t.bid.drywallGroup}>
                        {drywallServices.map((s) => (
                          <option key={s.slug} value={s.slug}>
                            {t.services.items[s.slug].title}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="R3 Services">
                        {r3Services.map((s) => (
                          <option key={s.slug} value={s.slug}>
                            {t.r3.items[s.slug].title}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                <fieldset>
                  <legend className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.reviews.rating}
                  </legend>
                  <StarInput
                    value={form.rating}
                    onChange={(v) => setForm((f) => ({ ...f, rating: v }))}
                    testId="review-rating"
                  />
                </fieldset>

                <div>
                  <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                    {t.reviews.reviewText}
                  </label>
                  <textarea
                    data-testid="review-text-input"
                    required
                    rows={5}
                    value={form.review}
                    onChange={(e) => setForm((f) => ({ ...f, review: e.target.value }))}
                    placeholder={t.reviews.reviewPh}
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t.reviews.city}
                    </label>
                    <input
                      data-testid="review-city-input"
                      value={form.city}
                      onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400">
                      {t.reviews.contact}
                    </label>
                    <input
                      data-testid="review-contact-input"
                      value={form.contact}
                      onChange={(e) => setForm((f) => ({ ...f, contact: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <label
                  data-testid="review-confirm-label"
                  className="flex cursor-pointer items-start gap-3 border border-white/10 bg-charcoal p-5"
                >
                  <input
                    data-testid="review-confirm-checkbox"
                    type="checkbox"
                    checked={form.confirmed}
                    onChange={(e) => setForm((f) => ({ ...f, confirmed: e.target.checked }))}
                    className="mt-0.5 h-5 w-5 shrink-0"
                  />
                  <span className="text-sm leading-relaxed text-neutral-300">
                    {t.reviews.confirm}
                  </span>
                </label>

                <button
                  data-testid="review-submit-btn"
                  type="submit"
                  disabled={status === "sending"}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-black uppercase tracking-[0.12em] text-ink transition-colors duration-300 hover:bg-golddark disabled:opacity-60 sm:w-auto"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t.reviews.submitting}
                    </>
                  ) : (
                    t.reviews.submit
                  )}
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </main>
  );
}
